import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { Highlight } from 'react-instantsearch';

const markSx = {
  '& mark': { backgroundColor: 'transparent', color: 'primary.dark' },
};

export default function CategoryRail({
  categories,
  heading,
  variant,
  idPrefix,
  highlightedIndex,
  onSelect,
  onMouseEnter,
  sx,
}) {
  if (!categories.length) return null;

  const isPills = variant === 'pills';

  return (
    <Box
      sx={[
        isPills
          ? { pb: 0.5 }
          : {
              flexBasis: 220,
              flexShrink: 0,
              borderRight: '1px solid',
              borderColor: 'grayscale.200',
              pb: 1.5,
              overflowY: 'auto',
            },
        sx,
      ]}>
      <Typography
        variant="overline"
        component="h3"
        sx={{
          display: 'block',
          px: 2,
          pt: 1.75,
          pb: 0.75,
          color: 'text.secondary',
        }}>
        {heading}
      </Typography>

      <Box
        sx={
          isPills
            ? {
                display: 'flex',
                gap: 1,
                px: 2,
                overflowX: 'auto',
                flexWrap: 'nowrap',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }
            : { display: 'flex', flexDirection: 'column' }
        }>
        {categories.map((category, index) => (
          <Box
            key={category.objectID || category.name}
            id={`${idPrefix}-category-${index}`}
            role="option"
            aria-selected={highlightedIndex === index}
            component="button"
            type="button"
            data-test-subject="category-link"
            tabIndex={-1}
            onClick={() => onSelect(category)}
            onMouseEnter={() => onMouseEnter?.(index)}
            sx={{
              ...markSx,
              cursor: 'pointer',
              font: 'inherit',
              textAlign: 'left',
              color: 'primary.main',
              background: 'none',
              minHeight: 44,
              display: 'flex',
              alignItems: 'center',
              backgroundColor:
                highlightedIndex === index ? 'action.hover' : 'transparent',
              ...(isPills
                ? {
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    px: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '22px',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }
                : {
                    px: 2,
                    border: 'none',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }),
            }}>
            {category?._highlightResult?.name ? (
              <Highlight hit={category} attribute="name" />
            ) : (
              category.name
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

CategoryRail.propTypes = {
  categories: PropTypes.array.isRequired,
  heading: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(['rail', 'pills']).isRequired,
  idPrefix: PropTypes.string.isRequired,
  highlightedIndex: PropTypes.number,
  onSelect: PropTypes.func.isRequired,
  onMouseEnter: PropTypes.func,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

CategoryRail.defaultProps = {
  highlightedIndex: -1,
};
